import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import styles from "./styles/ProjectFeatures.module.css";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import CommonCardFeatureSegment from "Components/FeatureFlip/Common/Cards/CommonCardFeatureSegment";
import EmptyState from "Components/FeatureFlip/Common/EmptyState/EmptyState";
import { useGetAllFeatures } from "API/Queries/useGetAllFeatures";
import { Feature } from "Store/stateInterfaces";
import { ROUTES } from "Utils/routes";
import { searchQuery } from "Utils/constants";

const ProjectFeatures = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const observerRef = useRef<HTMLDivElement>(null);

  const pageFeatures = useFeatureFlipStore((s) => s.pageFeatures);
  const setPageFeatures = useFeatureFlipStore((s) => s.setPageFeatures);

  const features = useFeatureFlipStore((s) => s.features);
  const setIsFeatureDynamic = useFeatureFlipStore((s) => s.setIsFeatureDynamic);
  const setIsFeatureEnabled = useFeatureFlipStore((s) => s.setIsFeatureEnabled);
  const setFeatures = useFeatureFlipStore((s) => s.setFeatures) as (
    features: Feature[] | ((prev: Feature[]) => Feature[])
  ) => void;
  const searchTerm = useFeatureFlipStore((s) => s.searchTerm);
  const hasMoreDataFeatures = useFeatureFlipStore((s) => s.hasMoreDataFeatures);
  const setHasMoreDataFeatures = useFeatureFlipStore(
    (s) => s.setHasMoreDataFeatures
  );
  const setSearchTerm = useFeatureFlipStore((s) => s.setSearchTerm);
  const limit = 10;
  const offSet = (pageFeatures - 1) * limit;

  const { refetch: getAllFeatures } = useGetAllFeatures(
    {
      pageSize: limit,
      query: searchQuery(id, searchTerm),
      pageNumber: pageFeatures,
    },
    {
      onSuccess: (data: Feature[]) => {
        if (data.length < limit) {
          setHasMoreDataFeatures(false);
        }
        if (pageFeatures === 1) {
          setFeatures(data);
        } else {
          setFeatures([...features, ...data]);
        }
      },
    }
  );

  useEffect(() => {
    if (pageFeatures !== 1) getAllFeatures();
  }, [pageFeatures]);

  // Reset when search changes
  useEffect(() => {
    if (!searchTerm) {
      setHasMoreDataFeatures(true);
      setFeatures([]);
    }
    setPageFeatures(1);

    const debounceTimer = setTimeout(() => {
      window.scroll(0, 0);
      getAllFeatures();
      // re-enable after fetch
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Infinite scroll observer
  useEffect(() => {
    if (!observerRef.current || !hasMoreDataFeatures) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPageFeatures(pageFeatures + 1);
      }
    });

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [hasMoreDataFeatures, features]);

  useEffect(() => {
    return () => {
      window.scroll(0, 0);
      setPageFeatures(1);
      setHasMoreDataFeatures(true);
      setSearchTerm("");
    };
  }, []);

  const handleFeatureClick = (
    featureId: string,
    name?: string,
    additionalItem?: any
  ) => {
    navigate(ROUTES.FEATURE_DETAILS(id as string, featureId), {
      state: { name },
    });
    setIsFeatureDynamic(additionalItem?.dynamic);
    setIsFeatureEnabled(additionalItem?.enabled);
  };

  return (
    <div className={styles.pageContainer}>
      {features?.length ? (
        <div className={styles.featuresGrid}>
          {features.map((feature) => (
            <CommonCardFeatureSegment
              key={feature.id}
              {...feature}
              handleClick={handleFeatureClick}
              btnTitle="View Feature"
            />
          ))}
          {hasMoreDataFeatures && (
            <div ref={observerRef} style={{ height: "1px" }} />
          )}
        </div>
      ) : (
        <EmptyState
          title="No Features Found"
          text="We couldn't find any features matching your search. Try adjusting your search terms or create a new feature."
        />
      )}
    </div>
  );
};

export default ProjectFeatures;
